import * as React from "react";
import { Upload, X, Link as LinkIcon, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/hooks/useI18n";
import { MediaItem, normalizeMedia, toMediaItem } from "@/types/media.types";
import { UnsplashAttribution } from "@/components/ui/UnsplashAttribution";
import { UnsplashPicker } from "@/components/ui/UnsplashPicker";

interface UploadMultiImagesProps {
  /** Accetta anche il formato legacy (array di URL): viene normalizzato in lettura. */
  images: MediaItem[] | string[];
  onImagesChange: (images: MediaItem[]) => void;
  itineraryId: string;
  activityId?: string; // Optional: if provided, saves to activities-data path
  label?: string;
  maxImages?: number;
  className?: string;
  hasError?: boolean;
  /** Sola lettura: nasconde i controlli di upload/URL/Unsplash e la rimozione immagini. */
  readOnly?: boolean;
}

const UploadMultiImages = React.forwardRef<HTMLDivElement, UploadMultiImagesProps>(
  ({ images: rawImages, onImagesChange, itineraryId, activityId, label, maxImages, className, hasError = false, readOnly = false, ...props }, ref) => {
    const { t } = useI18n();
    // Retrocompatibilità: le attività salvate prima del modello MediaItem hanno
    // `media` come array di stringhe. Normalizziamo una volta sola qui.
    const images = React.useMemo(() => normalizeMedia(rawImages), [rawImages]);
    const [uploading, setUploading] = React.useState(false);
    const [showUrlInput, setShowUrlInput] = React.useState(false);
    const [imageUrl, setImageUrl] = React.useState('');
    const [addingUrl, setAddingUrl] = React.useState(false);
    const [showUnsplash, setShowUnsplash] = React.useState(false);

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      setUploading(true);
      const uploaded: MediaItem[] = [];

      try {
        for (const file of Array.from(files)) {
          // Generate unique filename
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
          // Se è presente activityId, salva in activities, altrimenti in media
          const filePath = activityId
            ? `itineraries/${itineraryId}/activities/${activityId}/media/${fileName}`
            : `itineraries/${itineraryId}/media/${fileName}`;

          // Upload to Supabase Storage
          const { data, error } = await supabase.storage
            .from('p2b')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (error) {
            console.error('Upload error:', error);
            alert(`Failed to upload ${file.name}: ${error.message}`);
            continue;
          }

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('p2b')
            .getPublicUrl(filePath);

          uploaded.push(toMediaItem(publicUrl, 'storage'));
        }

        const newImages = [...images, ...uploaded];
        const finalImages = maxImages ? newImages.slice(0, maxImages) : newImages;
        onImagesChange(finalImages);
      } catch (error) {
        console.error('Error uploading images:', error);
        alert('An error occurred while uploading images');
      } finally {
        setUploading(false);
      }
    };

    const handleAddImageUrl = async () => {
      if (!imageUrl.trim()) {
        alert('Please enter an image URL');
        return;
      }

      setAddingUrl(true);
      try {
        // Validate that URL is accessible and is an image
        const response = await fetch(imageUrl, { method: 'HEAD' });
        if (!response.ok || !response.headers.get('content-type')?.startsWith('image/')) {
          alert('Invalid image URL. Please enter a valid image URL.');
          setAddingUrl(false);
          return;
        }

        const newImages = [...images, toMediaItem(imageUrl)];
        const finalImages = maxImages ? newImages.slice(0, maxImages) : newImages;
        onImagesChange(finalImages);
        setImageUrl('');
        setShowUrlInput(false);
      } catch (error) {
        console.error('Error validating image URL:', error);
        alert('Could not validate image URL. Please check if the URL is correct and accessible.');
      } finally {
        setAddingUrl(false);
      }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleAddImageUrl();
      }
    };

    const handleSelectUnsplashPhoto = (item: MediaItem) => {
      if (isMaxReached) return;
      const newImages = [...images, item];
      onImagesChange(maxImages ? newImages.slice(0, maxImages) : newImages);
    };

    const removeImage = async (index: number) => {
      const imageUrl = images[index]?.url;

      // Try to delete from storage if it's a Supabase URL
      if (imageUrl && imageUrl.includes('supabase')) {
        try {
          // Extract file path from URL
          const urlParts = imageUrl.split('/storage/v1/object/public/p2b/');
          if (urlParts.length > 1) {
            const filePath = urlParts[1];
            await supabase.storage
              .from('p2b')
              .remove([filePath]);
          }
        } catch (error) {
          console.error('Error deleting image from storage:', error);
        }
      }

      const newImages = images.filter((_, i) => i !== index);
      onImagesChange(newImages);
    };

    // Ordinamento: la prima immagine dell'array è la copertina dell'attività.
    const [dragIndex, setDragIndex] = React.useState<number | null>(null);
    const [dropIndex, setDropIndex] = React.useState<number | null>(null);
    const canReorder = !readOnly && images.length > 1;

    const moveImage = (from: number, to: number) => {
      if (from === to || from < 0 || to < 0 || from >= images.length || to >= images.length) return;
      const newImages = [...images];
      const [moved] = newImages.splice(from, 1);
      newImages.splice(to, 0, moved);
      onImagesChange(newImages);
    };

    const resetDrag = () => {
      setDragIndex(null);
      setDropIndex(null);
    };

    const isMaxReached = maxImages ? images.length >= maxImages : false;

    return (
      <div ref={ref} className={className} {...props}>
        {!readOnly && (
        <div>
          <Label htmlFor="image-upload" className={`text-sm ${hasError ? "text-yellow-600" : ""}`}>{label ?? t('fileUpload.defaultImageLabel')}</Label>
          <div className="mt-2 flex gap-2">
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              disabled={isMaxReached}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => document.getElementById('image-upload')?.click()}
              disabled={isMaxReached || uploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploading
                ? t('modals.uploadingImages')
                : isMaxReached
                  ? t('modals.maxImages', { count: maxImages })
                  : t('modals.chooseImages')}
            </Button>

            {/* Add from URL Button */}
            {!showUrlInput && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isMaxReached}
                onClick={() => setShowUrlInput(true)}
              >
                <LinkIcon className="mr-2 h-4 w-4" />
                {t('modals.addFromUrl')}
              </Button>
            )}

            {/* Search Unsplash Button */}
            {!showUnsplash && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isMaxReached}
                onClick={() => setShowUnsplash(true)}
              >
                <Search className="mr-2 h-4 w-4" />
                {t('modals.searchUnsplash')}
              </Button>
            )}
          </div>

          {/* URL Input Section */}
          {showUrlInput && (
            <div className="mt-3 space-y-2 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2">
                <LinkIcon className="h-4 w-4 text-gray-600" />
                <span className="font-medium text-gray-700">{t('modals.addImageFromUrl')}</span>
              </div>
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={addingUrl}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleAddImageUrl}
                  disabled={addingUrl || !imageUrl.trim()}
                >
                  {addingUrl ? t('modals.adding') : t('common.add')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setImageUrl(''); setShowUrlInput(false); }}
                  disabled={addingUrl}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Unsplash Search Section */}
          {showUnsplash && (
            <UnsplashPicker
              className="mt-3"
              disabled={isMaxReached}
              onSelect={handleSelectUnsplashPhoto}
              onClose={() => setShowUnsplash(false)}
            />
          )}
        </div>
        )}

        {/* Images Preview — horizontal scrollable row */}
        {images && images.length > 0 ? (
          <div className="mt-4">
            {canReorder && (
              <p className="text-xs text-gray-500 mb-2">{t('modals.reorderImagesHint')}</p>
            )}
            <div className="flex flex-row items-center gap-2 overflow-x-auto pb-1 pt-1">
              {images.map((item, index) => {
                const isCover = index === 0;
                const isDropTarget = dropIndex === index && dragIndex !== null && dragIndex !== index;
                return (
                  <div
                    key={`${item.url}-${index}`}
                    className={`relative group flex-shrink-0 rounded-lg transition-opacity ${
                      canReorder ? 'cursor-grab active:cursor-grabbing' : ''
                    } ${dragIndex === index ? 'opacity-40' : ''} ${
                      isDropTarget ? 'ring-2 ring-offset-1 ring-blue-400' : ''
                    }`}
                    draggable={canReorder}
                    onDragStart={(e) => {
                      setDragIndex(index);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      if (dragIndex === null) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (dropIndex !== index) setDropIndex(index);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragIndex !== null) moveImage(dragIndex, index);
                      resetDrag();
                    }}
                    onDragEnd={resetDrag}
                  >
                    <img
                      src={item.url}
                      alt={item.alt || `Image ${index + 1}`}
                      draggable={false}
                      className={`object-cover rounded-lg ${
                        isCover && images.length > 1 ? 'w-28 h-28 border-2 border-primary' : 'w-20 h-20 border'
                      }`}
                    />
                    <UnsplashAttribution item={item} />
                    {!readOnly && (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        className="absolute top-1 right-1 z-20 w-5 h-5 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                        onClick={() => removeImage(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                    {canReorder && index > 0 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="absolute left-1 top-1/2 -translate-y-1/2 z-20 w-5 h-5 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                        title={t('modals.moveImageLeft')}
                        aria-label={t('modals.moveImageLeft')}
                        onClick={() => moveImage(index, index - 1)}
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </Button>
                    )}
                    {canReorder && index < images.length - 1 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 z-20 w-5 h-5 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                        title={t('modals.moveImageRight')}
                        aria-label={t('modals.moveImageRight')}
                        onClick={() => moveImage(index, index + 1)}
                      >
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          readOnly && <p className="text-sm text-gray-400 italic">{t('modals.noImages')}</p>
        )}
      </div>
    );
  }
);

UploadMultiImages.displayName = "UploadMultiImages";

export { UploadMultiImages };
